import { PROCESS_ASSIGN_RULES, type ProcessAssignRule, type ProcessTaskSettingDto, STAFF_FUNCTIONS, TRADE_RULES, type TradeRule } from '@bqcrm/shared';
import { Badge, Button, Card, Code, Group, NumberInput, Select, Stack, Text } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../api';
import { PageHeader } from '../components/PageHeader';
import { QueryState } from '../components/QueryState';
import { UnsavedHint } from '../components/ui';
import { useOfficeUsers } from '../data';
import { useSaveShortcut } from '../hooks';
import { notifyError, notifyOk } from '../notify';

/** Pola zapisywane przez PUT /settings/process — do porównania z danymi z serwera. */
const editable = (rows: ProcessTaskSettingDto[]) => JSON.stringify(rows.map((r) => [r.type, r.rule, r.tradeRule, r.userId, r.slaDays]));

/**
 * Ustawienia zadań procesowych (docs/10 §3.2): komu domyślnie trafia zadanie danego typu i w jakim terminie.
 * Kto w ogóle może je wykonać — wynika z uprawnień (Role i uprawnienia), nie z tej strony.
 */
export function TaskTypesSettingsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['process-settings'], queryFn: () => api<ProcessTaskSettingDto[]>('/settings/process') });
  const users = useOfficeUsers();
  const [rows, setRows] = useState<ProcessTaskSettingDto[]>([]);
  useEffect(() => {
    if (q.data) setRows(q.data);
  }, [q.data]);
  const set = (type: string, patch: Partial<ProcessTaskSettingDto>) => setRows((r) => r.map((x) => (x.type === type ? { ...x, ...patch } : x)));
  const save = useMutation({
    mutationFn: () => api<ProcessTaskSettingDto[]>('/settings/process', { method: 'PUT', body: { types: rows.map((r) => ({ type: r.type, rule: r.rule, tradeRule: r.tradeRule, userId: r.userId, slaDays: r.slaDays })) } }),
    onSuccess: (d) => {
      qc.setQueryData(['process-settings'], d);
      notifyOk('Zapisano ustawienia zadań');
    },
    onError: notifyError,
  });
  const dirty = !!q.data && editable(rows) !== editable(q.data);
  useSaveShortcut(dirty && !save.isPending, () => save.mutate());

  return (
    <>
      <PageHeader
        title="Zadania w sprawach"
        description="Komu domyślnie trafia zadanie i w jakim terminie. Zadania powstają same ze stanu sprawy (np. po akceptacji oferty — przygotowanie umowy). Kto może przejąć zadanie z kolejki — wynika z roli — jej uprawnień i kolejki zadań — oraz branż ustawionych na karcie pracownika („Pracownicy → karta pracownika → Dostęp”)."
      />
      <QueryState isLoading={q.isLoading} error={q.error}>
        <Stack gap="sm">
          {rows.map((r) => (
            <Card key={r.type} withBorder padding="md" style={{ opacity: r.active ? 1 : 0.6 }}>
              <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
                <Stack gap={4} style={{ flex: '1 1 320px' }}>
                  <Group gap="xs">
                    <Text fw={600}>{r.name}</Text>
                    {!r.active && (
                      <Badge size="xs" variant="light" color="gray">
                        w przygotowaniu
                      </Badge>
                    )}
                  </Group>
                  <Text size="sm" c="dimmed">
                    {r.description}
                  </Text>
                  <Text size="xs" c="dimmed">
                    Wykonać może pracownik z uprawnieniem <Code fz="xs">{r.permission}</Code> i rolą z kolejką{' '}
                    <Text span inherit fw={600}>
                      {STAFF_FUNCTIONS[r.staffFunction]}
                    </Text>
                    {r.tradeRule !== 'brak' ? ' w branży zadania' : ''} („Pracownicy → karta pracownika → Dostęp”).
                  </Text>
                </Stack>
                <Group gap="sm" align="flex-end" wrap="wrap">
                  <Select
                    label="Komu domyślnie"
                    w={{ base: '100%', sm: 260 }}
                    data={r.rules.map((x) => ({ value: x, label: PROCESS_ASSIGN_RULES[x] }))}
                    value={r.rule}
                    allowDeselect={false}
                    disabled={r.rules.length === 1}
                    onChange={(v) => v && set(r.type, { rule: v as ProcessAssignRule })}
                  />
                  {r.rule === 'osoba' && (
                    <Select label="Pracownik" w={{ base: '100%', sm: 200 }} data={(users.data ?? []).map((u) => ({ value: u.id, label: u.fullName }))} value={r.userId} onChange={(v) => set(r.type, { userId: v })} searchable />
                  )}
                  {r.tradeRules.length > 1 && (
                    <Select
                      label="Sprawa z kilkoma branżami"
                      w={{ base: '100%', sm: 300 }}
                      data={r.tradeRules.map((x) => ({ value: x, label: TRADE_RULES[x] }))}
                      value={r.tradeRule}
                      allowDeselect={false}
                      onChange={(v) => v && set(r.type, { tradeRule: v as TradeRule })}
                    />
                  )}
                  <NumberInput label={r.reminder ? 'Przypomnij po (dniach)' : 'Termin (dni)'} w={{ base: '100%', sm: r.reminder ? 150 : 110 }} min={1} max={365} value={r.slaDays ?? ''} placeholder={r.reminder ? 'nie przypominaj' : 'bez'} onChange={(v) => set(r.type, { slaDays: typeof v === 'number' ? v : null })} />
                </Group>
              </Group>
            </Card>
          ))}
          <Group justify="flex-end" pos="sticky" bottom={0} py="sm" wrap="wrap" style={{ background: 'var(--app-bg)', borderTop: '1px solid var(--app-border)' }}>
            <div style={{ marginRight: 'auto' }}>
              <UnsavedHint show={dirty} action="zaczęły obowiązywać" />
            </div>
            <Button variant="default" disabled={!dirty} onClick={() => q.data && setRows(q.data)}>
              Przywróć
            </Button>
            <Button disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>
              Zapisz
            </Button>
          </Group>
        </Stack>
      </QueryState>
    </>
  );
}
